import React from 'react';
import {apiConfig} from '@stencil/hotels-config';
import SomethingWentWrong from '@stencil/something-went-wrong';
import {defaultTheme} from '@stencil/build-theme';
import pushToNewrelic from '@stencil/push-to-new-relic-api';

interface AppErrorBoundaryProps {
  appName: string;
  SomethingWentWrongComponent?: React.FC<any>;
}

export default class AppErrorBoundary extends React.Component<AppErrorBoundaryProps> {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
    };
  }

  componentDidCatch(error, errorInfo) {
    pushToNewrelic(`serviceApp=${this.props.appName}&errorMsg=${error}`);
    if (process.env.BUILD_ENV === 'prod' && window.Sentry) {
      window.Sentry.withScope(scope => {
        Object.keys(errorInfo).forEach(key => {
          scope.setExtra(key, errorInfo[key]);
        });
        window.Sentry.captureException(error);
      });
    }
  }

  render() {
    /** 75 is magic number for height of header. Since it'll take figuring
     * out how to pass a ref from here to header, keeping it a magic number for now
     */
    const containerHeight = `calc(100vh - ${75}px)`;

    if (this.state.hasError) {
      if (this.props.SomethingWentWrongComponent) {
        return <this.props.SomethingWentWrongComponent />;
      }

      return (
        <SomethingWentWrong
          headingStyles={{
            fontFamily: defaultTheme.font.fontFamily,
          }}
          containerStyles={{
            height: containerHeight,
          }}
        />
      );
    }

    return this.props.children;
  }
}

type ComponentType = React.FC<AppErrorBoundaryProps>;

export function withAppErrorBoundary(
  Component: ComponentType,
  appBoundaryProps: AppErrorBoundaryProps,
) {
  return function(props: any) {
    return (
      <AppErrorBoundary {...appBoundaryProps}>
        <Component {...props} />
      </AppErrorBoundary>
    );
  };
}
